import { OrderAction, PermissionKey } from 'hx-services';
import { Params } from '@angular/router';
import { WORKPLACE_READY } from '@callcenter-app/service/workplace.config';

/** One row of the menu of the call-centre: a section (`isSub`) or a page. The old menu (`SidebarComponent`) and the new one (`ShellSidebarComponent`) read the same list — the same pages, rights and order. */
export interface MenuItem {
  title: string;
  isSub?: boolean;
  icon: string;
  link?: string;
  permissions?: PermissionKey[];
  queryParams?: Params;
  visible: boolean;
  /** Раздел с таким group сворачивается кликом по заголовку, его пункты скрыты, пока он свёрнут. */
  group?: string;
}

/** A fresh list each time: the menus switch `visible` of the rows with rights on their own copy. */
export function buildMainMenu(): MenuItem[] {
  return [
    {
      title: 'mainmenu.call-center',
      isSub: true,
      icon: 'ellipsis-h',
      visible: true
    },
    {
      // «Рабочее место» (слово владельца, 16.10) — пока рабочее место включено; без него на /call-center старая страница, и пункт называется, как и был, «Call-center»
      title: WORKPLACE_READY ? 'mainmenu.workplace' : 'mainmenu.call-center',
      link: 'call-center',
      icon: 'desktop',
      visible: true
    },
    {
      // прежняя страница рабочего места — сразу под новой, в основной группе (слово владельца, 16.10); только пока рабочее место включено
      title: 'mainmenu.call-center-old',
      link: 'call-center-old',
      icon: 'desktop',
      visible: WORKPLACE_READY
    },
    {
      // Без permissions: оператор видит только себя, бэкенд берёт его из токена.
      title: 'mainmenu.my-sales',
      link: 'my-sales',
      icon: 'money',
      visible: true
    },
    {
      title: 'mainmenu.orders',
      link: 'orders',
      icon: 'list-ul',
      visible: true
    },
    {
      title: 'mainmenu.control',
      isSub: true,
      icon: 'ellipsis-h',
      visible: true
    },
    {
      title: 'mainmenu.orders-archive',
      link: 'orders-archive',
      icon: 'archive',
      visible: true
    },
    {
      title: 'mainmenu.calls-new',
      link: 'calls-new',
      icon: 'phone',
      visible: true
    },
    {
      title: 'mainmenu.clients',
      link: 'clients',
      icon: 'users',
      visible: true
    },
    {
      title: 'mainmenu.faq',
      link: 'faq',
      icon: 'question-circle',
      visible: true,
    },
    {
      title: 'mainmenu.users',
      link: 'users',
      permissions: [PermissionKey.callcenter_manager],
      icon: 'user-tie',
      visible: false,
    },
    {
      title: 'mainmenu.reports',
      isSub: true,
      permissions: [PermissionKey.callcenter_view_report],
      icon: 'ellipsis-h',
      visible: false
    },
    {
      title: 'mainmenu.report.call-funnel',
      link: 'report/call-funnel',
      permissions: [PermissionKey.callcenter_view_report],
      icon: 'filter',
      visible: false
    },
    {
      title: 'mainmenu.report.load',
      link: 'report/load',
      permissions: [PermissionKey.callcenter_view_report],
      icon: 'line-chart',
      visible: false
    },
    {
      title: 'mainmenu.report.operator-summary',
      link: 'report/operator-summary',
      permissions: [PermissionKey.callcenter_view_report],
      icon: 'bar-chart',
      visible: false
    },
    {
      title: 'mainmenu.feedbacks-new',
      link: 'feedbacks-new',
      permissions: [PermissionKey.callcenter_view_report],
      icon: 'star-o',
      visible: false
    },
    {
      title: 'mainmenu.report.rejections',
      link: 'rejections',
      icon: 'comments-o',
      visible: false
    },
    {
      title: 'mainmenu.report.monitoring',
      link: 'report/monitoring',
      icon: 'chart-line',
      permissions: [PermissionKey.callcenter_view_report],
      visible: false
    },
    {
      title: 'mainmenu.old-pages',
      isSub: true,
      icon: 'ellipsis-h',
      group: 'old-pages',
      visible: true
    },
    {
      // Старый список заказов — первым в «Старых страницах» (слово владельца, 16.10; до этого стоял рядом с новым «Заказы»). Путь, значок, параметры и права те же, что были:
      // подсветка по точному пути /orders/list, на /orders горит только новый пункт.
      title: 'mainmenu.orders-old',
      link: 'orders/list',
      icon: 'list-ul',
      queryParams: {activeCart: false},
      group: 'old-pages',
      visible: true
    },
    {
      title: 'mainmenu.orders-archive-old',
      link: 'orders/archive',
      icon: 'archive',
      queryParams: {activeCart: false, actions: OrderAction.RECEIVED},
      group: 'old-pages',
      visible: true
    },
    {
      title: 'mainmenu.clients-old',
      link: 'clients-old',
      icon: 'users',
      group: 'old-pages',
      visible: true
    },
    {
      title: 'mainmenu.calls',
      link: 'calls',
      icon: 'history',
      group: 'old-pages',
      visible: true
    },
    {
      title: 'mainmenu.report.feedback',
      link: 'orders/feedbacks',
      permissions: [PermissionKey.callcenter_view_report],
      icon: 'comments-o',
      group: 'old-pages',
      visible: false
    },
    {
      title: 'mainmenu.report.calls',
      link: 'report/calls',
      permissions: [PermissionKey.callcenter_view_report],
      icon: 'phone-square',
      group: 'old-pages',
      visible: false
    },
    {
      title: 'mainmenu.report.operator-order-stat',
      link: 'report/operator-order-stat',
      permissions: [PermissionKey.callcenter_view_report],
      icon: 'list-alt',
      group: 'old-pages',
      visible: false
    },
    {
      title: 'mainmenu.report.feedback-rating',
      link: 'report/feedback-rating',
      permissions: [PermissionKey.callcenter_view_report],
      icon: 'line-chart',
      group: 'old-pages',
      visible: false
    },
    {
      title: 'mainmenu.report.calls-status',
      link: 'report/calls-status',
      permissions: [PermissionKey.callcenter_view_report],
      icon: 'tty',
      group: 'old-pages',
      visible: false
    },
    {
      title: 'mainmenu.report.calls-hours',
      link: 'report/calls-hours',
      permissions: [PermissionKey.callcenter_view_report],
      icon: 'hourglass-half',
      group: 'old-pages',
      visible: false
    },
    {
      title: 'mainmenu.faq-old',
      link: 'faq/list',
      icon: 'bell',
      group: 'old-pages',
      visible: true
    }
  ];
}
